import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { authApiClient } from '../../../utils/axios';

const OrganizationMfaModal = ({
                                  open,
                                  email,
                                  organizationId,
                                  organizationName,
                                  onVerified,
                                  onCancel,
                                  submitting = false,
                                  setSubmitting,
                              }) => {
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [error, setError] = useState(null);
    const [timer, setTimer] = useState(120);
    const inputRefs = useRef([]);

    useEffect(() => {
        if (!open) {
            setOtp(['', '', '', '', '', '']);
            setError(null);
            setTimer(120);

            return;
        }

        setTimeout(() => {
            inputRefs.current[0]?.focus();
        }, 100);
    }, [open]);

    useEffect(() => {
        if (!open || timer <= 0) {
            return undefined;
        }

        const countdown = setInterval(() => {
            setTimer((current) => current - 1);
        }, 1000);

        return () => clearInterval(countdown);
    }, [open, timer]);

    const handleOtpChange = (index, value) => {
        if (value.length > 1) {
            return;
        }

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);
        setError(null);

        if (value && index < 5) {
            requestAnimationFrame(() => {
                const nextInput = inputRefs.current[index + 1];
                if (nextInput) {
                    nextInput.focus();
                    nextInput.select();
                }
            });
        }
    };

    const handlePaste = (event) => {
        event.preventDefault();
        const pastedData = event.clipboardData.getData('text');

        if (/^\d{6}$/.test(pastedData)) {
            setOtp(pastedData.split(''));
            setError(null);

            setTimeout(() => {
                inputRefs.current[5]?.focus();
            }, 100);
        }
    };

    const handleKeyDown = (index, event) => {
        if (event.key === 'Backspace') {
            if (otp[index]) {
                const newOtp = [...otp];
                newOtp[index] = '';
                setOtp(newOtp);
            } else if (index > 0) {
                inputRefs.current[index - 1]?.focus();
            }
        }

        if (event.key === 'Enter' && otp.join('').length === 6) {
            handleVerify();
        }
    };

    const handleVerify = async () => {
        const otpString = otp.join('');

        if (!email || otpString.length !== 6) {
            setError( 'Please enter the complete 6-digit OTP');

            return;
        }

        setSubmitting?.(true);
        setError(null);

        try {
            const response = await authApiClient.post('/verify-mfa', {
                email,
                otp: otpString,
            });

            if (!response.data?.token) {
                setError( 'Unable to verify OTP. Please try again.');

                return;
            }

            await onVerified?.(response.data.token);
        } catch (err) {
            setError(
                err?.response?.data?.errors?.otp ||
                err?.response?.data?.errors?.message ||
                err?.response?.data?.message ||
                'Unable to verify OTP. Please try again.',
            );
        } finally {
            setSubmitting?.(false);
        }
    };

    const handleResend = async () => {
        if (!organizationId) {
            return;
        }

        setError(null);

        try {
            await authApiClient.post(`/organizations/${organizationId}/token`);
            setOtp(['', '', '', '', '', '']);
            setTimer(120);

            setTimeout(() => {
                inputRefs.current[0]?.focus();
            }, 100);
        } catch (err) {
            setError(
                err?.response?.data?.errors?.message ||
                err?.response?.data?.message ||
                'Unable to resend OTP. Please try again.',
            );
        }
    };

    const subtitle = ('Enter the OTP sent to your email for {organization}.').replace(
        '{organization}',
        organizationName || 'this organization',
    );

    return (
        <Dialog
            open={open}
            onClose={onCancel}
            maxWidth="xs"
            fullWidth={false}
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    width: '435px',
                    maxWidth: '90vw',
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 1,
                }}
            >
                { 'Verify your identity'}
                {onCancel ? (
                    <IconButton onClick={onCancel} size="small" disabled={submitting} aria-label="close">
                        <Close />
                    </IconButton>
                ) : null}
            </DialogTitle>

            <DialogContent sx={{ pt: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {subtitle}
                </Typography>

                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        {otp.map((digit, index) => (
                            <TextField
                                key={index}
                                inputRef={(element) => {
                                    inputRefs.current[index] = element;
                                }}
                                value={digit}
                                onChange={(event) => handleOtpChange(index, event.target.value.replace(/\D/g, ''))}
                                onKeyDown={(event) => handleKeyDown(index, event)}
                                onPaste={handlePaste}
                                variant="outlined"
                                inputProps={{
                                    maxLength: 1,
                                    inputMode: 'numeric',
                                    style: {
                                        textAlign: 'center',
                                        fontSize: '1.2rem',
                                        fontWeight: 'bold',
                                        width: '32px',
                                        height: '32px',
                                    },
                                }}
                                disabled={submitting}
                                error={Boolean(error)}
                                sx={{
                                    width: '56px',
                                    margin: 0,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: index === 0 ? '4px 0 0 4px' : index === 5 ? '0 4px 4px 0' : 0,
                                        '& fieldset': {
                                            borderColor: error ? 'error.main' : 'grey.300',
                                            borderRightWidth: index === 5 ? 1 : 0,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: error ? 'error.main' : 'grey.400',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: error ? 'error.main' : 'primary.main',
                                        },
                                    },
                                }}
                            />
                        ))}
                    </Box>
                    {error ? (
                        <Typography variant="body2" color="error" sx={{ textAlign: 'center', mt: 1 }}>
                            {error}
                        </Typography>
                    ) : null}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 0, flexDirection: 'column', alignItems: 'center' }}>
                <Button
                    onClick={handleVerify}
                    variant="contained"
                    disabled={submitting || !email || otp.join('').length !== 6}
                    sx={{
                        width: '100%',
                        mb: 2,
                        fontWeight: 'bold',
                        py: 1.5,
                        textTransform: 'none',
                    }}
                >
                    {submitting ? ('Verifying...') : ( 'Verify and continue')}
                </Button>

                {timer > 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                        {( 'Resend OTP in {minutes}:{seconds}')
                            .replace('{minutes}', String(Math.floor(timer / 60)))
                            .replace('{seconds}', String(timer % 60).padStart(2, '0'))}
                    </Typography>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                        {"Didn't receive the OTP?"}{' '}
                        <Button
                            variant="text"
                            onClick={handleResend}
                            disabled={submitting}
                            sx={{
                                p: 0,
                                minWidth: 'auto',
                                fontWeight: 'bold',
                                color: 'primary.main',
                                textTransform: 'none',
                            }}
                        >
                            {'Resend OTP'}
                        </Button>
                    </Typography>
                )}

                {onCancel ? (
                    <Button variant="text" onClick={onCancel} disabled={submitting} sx={{ mt: 1, textTransform: 'none' }}>
                        { 'Cancel'}
                    </Button>
                ) : null}
            </DialogActions>
        </Dialog>
    );
};

export default OrganizationMfaModal;